import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { MembershipService } from 'src/app/services/membership.service';
import { VippsPaymentStatus } from 'src/app/common/enums/enums';
import { NotificationService } from 'src/app/services/Notification.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { MembershipExpiryModel } from 'src/app/common/models/MemberAccess/MembershipExpiryModel';


@Component({
    selector: 'app-expire-message',
    templateUrl: './expire-message.component.html',
    styleUrls: ['./expire-message.component.scss'],
    standalone: false
})
export class ExpireMessageComponent implements OnInit, OnDestroy {

  customerID!: string;
  isInExpireRange: boolean = false;
  panelTitle!: string;
  panelMessage!: string;
  VippsPaymentStatus: typeof VippsPaymentStatus = VippsPaymentStatus;
  sub: Subscription | undefined;
  paymentStatus:VippsPaymentStatus;
  isPaymentCaptured: boolean = false;
  isAuthorized: boolean = false;
    // --- Renewal notification state ---
  showRenewalNotification: boolean = false;
  renewalExpiryDate: Date | null = null;
  @Output() expireStateChange = new EventEmitter<boolean>();
  private destroy$ = new Subject<void>();

  constructor(private authenticationService: AuthenticationService, private translate: TranslateService, private membershipService: MembershipService,
    private notificationService: NotificationService
  ) {
    this.customerID = this.authenticationService.getUser().CustomerId;
  }

  ngOnInit(): void {
    this.setExpireMessage();
    this.checkPaymentStatus();
    this.listenToPaymentStatusChange();
    this.listenToExpireDateChange();

    this.expireStateChange.emit(this.isInExpireRange);
  }


  private setExpireMessage(): void {
    const warningRanges = environment.TRIAL_EXPIRE_WARNING_RANGE;
    const sortedValues = Object.entries(warningRanges).sort((a, b) => a[1] - b[1]);
    const remainingDays = this.membershipService.getRemainingDays();
    const validRangeKey = this.getValidValueByRange(remainingDays, sortedValues);

    //Return if no valid range
    if (!validRangeKey) return;
    this.isInExpireRange = true;
    this.setExpireMessageText(validRangeKey, remainingDays);

  }

  redirectToWebShop(): void {
    this.authenticationService.setCookieShop(true);
    let url = environment.WEB_SHOP_MEMBERSHIP_URL.replace("{0}", this.authenticationService.getUser().CustomerId );
    window.location.href = url;
    //window.open(url, "_blank");
  }

  redirectWithExpireMessage(): void {
    this.redirectToWebShop();
  }

  getValidValueByRange(expireDateCount: number, valueRanges: Object): string {
    const entries = Object.entries(valueRanges);
    for (const [key, value] of entries) {
      if (expireDateCount <= value[1]) {
        return value[0];
      }
    }
    return null;
  }

  setExpireMessageText(key: string, dayCount: number): void {
    const dayCountText = dayCount.toString();//Consistent Formatting: The dayCount.toString() is stored in a dayCountText variable to avoid repeating the conversion and ensure consistency.

    this.panelTitle = dayCount === 0
        ? this.translate.instant('trial_expire_message.lbl_exp_today')
        : this.translate.instant(`trial_expire_message.${key}.lbl_title`).replace("{days}", dayCountText);
    this.panelMessage = this.translate.instant('trial_expire_message.' + key + '.lbl_prompt').replace("{days}", dayCount.toString());
  }

  checkPaymentStatus(): void {
      this.authenticationService.isPaymentInProgress(this.customerID).subscribe(isAuthorized => {
      this.isAuthorized = isAuthorized;
      if (this.isAuthorized) {
        this.paymentStatus = VippsPaymentStatus.Authorized;
         this.isInExpireRange = false;
       }
    });
  }

  listenToPaymentStatusChange(): void {
   // Subscribe to SignalR updates
   this.sub = this.notificationService.paymentStatus$
  .subscribe(update => {

    //Ignore updates for other customers
      if (update.customerId !== this.customerID) {
        return;
      }

    this.paymentStatus = update.status;

    // const expireVisible = !(this.paymentStatus === VippsPaymentStatus.Captured ||
    //                         this.paymentStatus === VippsPaymentStatus.Cancelled ||
    //                         this.paymentStatus === VippsPaymentStatus.Aborted ||
    //                         this.paymentStatus === VippsPaymentStatus.Expired ||
    //                         this.paymentStatus === VippsPaymentStatus.Terminated);

    //this.isInExpireRange = expireVisible;
    this.isPaymentCaptured = this.paymentStatus === VippsPaymentStatus.Captured;

    if (this.isPaymentCaptured) {
      this.membershipService.refreshExpireDateWithDelays();
    }

    //this.expireStateChange.emit(expireVisible);
  });
  }

private listenToExpireDateChange(): void {
  this.membershipService.membershipExpiration$
    .pipe(takeUntil(this.destroy$))
    .subscribe((data: MembershipExpiryModel) => {
      if (!data?.expireDate) return;

      const currentDateStr = this.toISODate(new Date(data.expireDate));
      const storageKey = this.expiryStorageKey;
      const storedDateStr = localStorage.getItem(storageKey);

      if (!storedDateStr) {
        localStorage.setItem(storageKey, currentDateStr);
        return;
      }

      if (currentDateStr <= storedDateStr) return;

      this.renewalExpiryDate = new Date(data.expireDate);
      this.showRenewalNotification = true;
    });
}

  dismissRenewalNotification(): void {
    this.showRenewalNotification = false;
    if (this.renewalExpiryDate) {
      localStorage.setItem(this.expiryStorageKey, this.toISODate(this.renewalExpiryDate));
    }
  }

  private toISODate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.sub?.unsubscribe();
  }

  private get expiryStorageKey(): string {
    return `last_known_expiry_${this.customerID}`;
  }
}
